<template>
  <div>
    <div class="card">
      <div class="flex justify-content-between align-items-center my-2">
        <h1 class="capitalize">{{ t('sidebar.portalManagement') }}</h1>
        <Button
          icon="pi pi-plus"
          :label="t('portalManagement.newAccountCreation')"
          @click="showCreateDialog = true"
        />
      </div>

      <Tabs :value="activeTab">
        <TabList>
          <Tab
            v-for="(tab, idx) in tabItems"
            :key="idx"
            :value="idx"
            @click="router.push(tab.route)"
          >
            <div class="flex align-items-center gap-2">
              <i :class="tab.icon" />
              <p>{{ tab.label }}</p>
            </div>
          </Tab>
        </TabList>
      </Tabs>

      <RouterView v-slot="{ Component }">
        <DataLoadingIndicator />
        <div class="p-3">
          <component :is="Component" />
        </div>
      </RouterView>
    </div>

    <CreateAccountPanel v-model:visible="showCreateDialog" />
  </div>
</template>

<script setup lang="ts">
import { ref, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Role } from '@/opendoors-api/client/types.gen'
import { useBreadcrumbs } from '@/composables/useBreadcrumbs'
import DataLoadingIndicator from '@/components/DataLoaderIndicator.vue'
import CreateAccountPanel from '@/domains/portal-management/CreateAccountPanel.vue'

definePage({
  name: 'account-management',
  meta: {
    requiredRoles: [Role.ADMIN]
  }
})

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { overrideBreadcrumb } = useBreadcrumbs()
const showCreateDialog = ref(false)

const pagePath = '/dashboard/account-management'

// This is a top-level page, not a detail page, so it doesn't need a breadcrumb. The
// routed tabs push the path past the store's depth<=2 auto-hide cutoff, so it would
// otherwise auto-generate one on every tab switch — clear it back out each time. Guard
// on still being inside this page's route: this watchEffect keeps reacting to
// route.path for one more tick when navigating away (before it's torn down with the
// rest of the component), and without the guard it would clobber the breadcrumb the
// destination page just set.
watchEffect(() => {
  if (!route.path.startsWith(pagePath)) return
  overrideBreadcrumb([])
})
const tabItems = ref([
  {
    route: `${pagePath}/companies`,
    label: t('portalManagement.tabs.companies'),
    icon: 'pi pi-building'
  },
  {
    route: `${pagePath}/accounts`,
    label: t('portalManagement.tabs.accounts'),
    icon: 'pi pi-users'
  }
])

const activeTab = ref(0)
watchEffect(() => {
  if (route.path === pagePath) {
    router.replace(tabItems.value[0].route)
    return
  }
  activeTab.value = tabItems.value.findIndex((tab) => route.path.includes(tab.route))
})
</script>
